import React, { useState } from 'react';
import { useApi, fmtKST, fmtNum, fmtPrice, holdOr } from '../hooks/useApi';
import BotAvatar from './BotAvatar';

const BOT_INFO = [
  {
    key: 'warren_buffett', label: '워런 버핏', initials: 'WB',
    color: 'bg-blue-600', border: 'border-blue-500',
    philosophy: '좋은 기업을 적정가에 사라.',
    tags: ['PER', 'PBR', 'ROE', '해자', '안전마진'],
    markets: 'KR, US',
  },
  {
    key: 'peter_lynch', label: '피터 린치', initials: 'PL',
    color: 'bg-green-600', border: 'border-green-500',
    philosophy: '아는 것에 투자하라.',
    tags: ['PEG', '이동평균', '성장률', 'GARP'],
    markets: 'KR, US',
  },
  {
    key: 'ray_dalio', label: '레이 달리오', initials: 'RD',
    color: 'bg-purple-600', border: 'border-purple-500',
    philosophy: '리스크 패리티로 모든 날씨를 대비하라.',
    tags: ['매크로', '60/180일선', '분산', '올웨더'],
    markets: 'KR, US',
  },
  {
    key: 'cathie_wood', label: '캐시 우드', initials: 'CW',
    color: 'bg-pink-600', border: 'border-pink-500',
    philosophy: '파괴적 혁신만이 미래다.',
    tags: ['혁신', '거래량', 'TAM', '파괴적기술'],
    markets: 'KR, US',
  },
  {
    key: 'jesse_livermore', label: '제시 리버모어', initials: 'JL',
    color: 'bg-yellow-600', border: 'border-yellow-500',
    philosophy: '추세를 따르라. 시장과 싸우지 마라.',
    tags: ['이동평균', '볼린저밴드', '거래량', '골든크로스'],
    markets: 'KR, US',
  },
];

export default function PersonaTab() {
  const [selectedBot, setSelectedBot] = useState(null);
  const { data: leaderboard } = useApi('/api/bots/leaderboard');

  const perfMap = {};
  (leaderboard || []).forEach((b) => { perfMap[b.bot_name] = b; });

  return (
    <div className="space-y-4">
      {/* 봇 페르소나 카드 5개 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        {BOT_INFO.map((bot) => {
          const perf = perfMap[bot.key] || {};
          const isActive = selectedBot === bot.key;
          return (
            <div
              key={bot.key}
              onClick={() => setSelectedBot(isActive ? null : bot.key)}
              className={`bg-gray-800 rounded-lg p-4 border-l-4 ${bot.border} cursor-pointer
                hover:bg-gray-750 transition ${isActive ? 'ring-2 ring-blue-500 bg-gray-750' : ''}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <BotAvatar botName={bot.key} size="md" />
                <div>
                  <div className="font-semibold text-sm">{bot.label}</div>
                  <div className="text-xs text-gray-500">{bot.markets}</div>
                </div>
              </div>

              <div className="text-xs text-gray-400 italic mb-3">"{bot.philosophy}"</div>

              <div className="flex flex-wrap gap-1 mb-3">
                {bot.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">{tag}</span>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-500">수익률</span>
                  <div className={`font-medium ${perf.total_return_pct == null ? 'text-yellow-300' : perf.total_return_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {holdOr(perf.total_return_pct, (v) => `${Number(v).toFixed(2)}%`)}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">승률</span>
                  <div className="font-medium">{perf.win_rate == null ? '— (표본 없음)' : `${Number(perf.win_rate).toFixed(1)}%`}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 봇 상세 패널 */}
      {selectedBot && <BotDetailPanel botKey={selectedBot} />}

      {/* 페르소나 타임라인 */}
      <PersonaTimeline selectedBot={selectedBot} />
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 봇 상세 패널
// ────────────────────────────────────────────────────────────
function BotDetailPanel({ botKey }) {
  const { data, loading } = useApi(`/api/persona/${botKey}/detail`, [botKey]);

  if (loading || !data) {
    return <div className="animate-pulse h-48 bg-gray-800 rounded-lg" />;
  }

  const botInfo = BOT_INFO.find((b) => b.key === botKey) || {};
  const perf = data.performance || {};

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      {/* ① 페르소나 정보 헤더 */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <BotAvatar botName={botKey} size="lg" />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold">{data.label}</h3>
              <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">{botInfo.markets}</span>
            </div>
            <div className="text-sm text-gray-400 italic mt-1">"{data.philosophy}"</div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(botInfo.tags || []).map((tag) => (
                <span key={tag} className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">{tag}</span>
              ))}
            </div>
          </div>
          <div className="hidden sm:grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-xs text-gray-500">수익률</div>
              <div className={`text-lg font-bold ${perf.total_return_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {holdOr(perf.total_return_pct, (v) => `${Number(v).toFixed(2)}%`)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">승률</div>
              <div className="text-lg font-bold">{perf.win_rate == null ? '— (표본 없음)' : `${Number(perf.win_rate).toFixed(1)}%`}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">거래수</div>
              <div className="text-lg font-bold">{perf.total_trades || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* 페르소나 상세 */}
      <PersonaDetailSection botKey={botKey} />

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-700">
        {/* ② 현재 포트폴리오 */}
        <div className="p-4">
          <h4 className="text-sm font-semibold text-gray-400 mb-3">보유 포트폴리오</h4>
          {data.quality === 'UNKNOWN' ? (<div className="text-yellow-300">보유 현황 산출 보류</div>) : (!data.positions || data.positions.length === 0) ? (
            <div className="text-sm text-gray-500 py-4 text-center">보유 종목 없음</div>
          ) : (
            <div className="space-y-2">
              {data.positions.map((pos, i) => {
                const pnlColor = pos.unrealized_pnl == null ? 'text-yellow-300' : pos.unrealized_pnl >= 0 ? 'text-green-400' : 'text-red-400';
                return (
                  <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-700/30">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{pos.ticker}</span>
                      <span className="text-gray-500">{pos.stock_name}</span>
                      {pos.market === 'US' && (
                        <span className="text-[10px] bg-blue-900/60 text-blue-300 px-1 rounded">US</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500">{fmtPrice(pos.avg_price, pos.market)}</span>
                      <span className="text-gray-300">{fmtPrice(pos.current_price, pos.market)}</span>
                      <span className={`font-medium ${pnlColor}`}>
                        {holdOr(pos.unrealized_pnl_pct, (v) => `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ③ 투자 결정 히스토리 */}
        <div className="p-4">
          <h4 className="text-sm font-semibold text-gray-400 mb-3">최근 투자 결정</h4>
          {(!data.verdicts || data.verdicts.length === 0) ? (
            <div className="text-sm text-gray-500 py-4 text-center">결정 이력 없음</div>
          ) : (
            <VerdictTimeline verdicts={data.verdicts} />
          )}
        </div>
      </div>

      {/* ④ 투자 신념 진화 */}
      <BeliefEvolution botKey={botKey} />
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// Verdict 타임라인 (접기/펼치기)
// ────────────────────────────────────────────────────────────
function VerdictTimeline({ verdicts }) {
  const [expanded, setExpanded] = useState({});

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const DECISION_STYLE = {
    BUY:  { bg: 'bg-green-500', text: 'text-green-400', label: 'BUY' },
    SELL: { bg: 'bg-red-500',   text: 'text-red-400',   label: 'SELL' },
    HOLD: { bg: 'bg-gray-500',  text: 'text-gray-400',  label: 'HOLD' },
  };

  return (
    <div className="max-h-80 overflow-y-auto space-y-0">
      <div className="relative pl-5">
        <div className="absolute left-2 top-0 bottom-0 w-px bg-gray-700" />
        {verdicts.map((v, i) => {
          const style = DECISION_STYLE[v.decision] || DECISION_STYLE.HOLD;
          const isOpen = expanded[v.verdict_id];
          return (
            <div key={v.verdict_id || i} className="relative py-2">
              <div className={`absolute left-0.5 w-3 h-3 rounded-full ${style.bg}`} style={{ top: '0.75rem' }} />
              <div className="ml-4">
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className={`font-bold ${style.text}`}>{style.label}</span>
                  <span className="text-blue-400">{v.ticker}</span>
                  <span className="text-gray-500">{v.stock_name}</span>
                  <span className="text-gray-600 ml-auto">{fmtKST(v.verdict_at)}</span>
                </div>

                {/* 확신도 바 */}
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 bg-gray-700 rounded-full h-1.5 max-w-[120px]">
                    <div
                      className={`h-1.5 rounded-full ${v.confidence >= 70 ? 'bg-green-500' : v.confidence >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(v.confidence, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-400">{Number(v.confidence).toFixed(0)}%</span>
                </div>

                {/* reasoning 펼치기 */}
                {v.reasoning && (
                  <button
                    onClick={() => toggle(v.verdict_id)}
                    className="text-xs text-gray-500 hover:text-gray-300 mt-1 transition"
                  >
                    {isOpen ? '접기' : '상세 보기'}
                  </button>
                )}
                {isOpen && v.reasoning && (
                  <div className="text-xs text-gray-300 mt-1 p-2 bg-gray-900 rounded whitespace-pre-wrap">
                    {v.reasoning}
                    {v.risk_note && (
                      <div className="text-yellow-500 mt-1">Risk: {v.risk_note}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 페르소나 상세 (철학/분석/매매/리스크/봇간관계 탭)
// ────────────────────────────────────────────────────────────
const PERSONA_TABS = [
  { key: 'philosophy', label: '철학' },
  { key: 'analysis', label: '분석방법' },
  { key: 'trading', label: '매매조건' },
  { key: 'risk', label: '리스크' },
  { key: 'relationships', label: '봇간관계' },
];

// 관계 분류: 협력/대립/보완
function classifyRelation(desc) {
  const d = desc || '';
  if (/의견 일치|상호 보완|협력|동시에 매수/.test(d)) return 'cooperate';
  if (/반대 성향|완전히 반대|대립|정반대/.test(d)) return 'oppose';
  return 'complement'; // 참고, 보조, 필터, 기타
}

const REL_STYLES = {
  cooperate:  { border: 'border-green-500', bg: 'bg-green-900/30', label: '협력', labelColor: 'text-green-400' },
  oppose:     { border: 'border-red-500',   bg: 'bg-red-900/30',   label: '대립', labelColor: 'text-red-400' },
  complement: { border: 'border-blue-500',  bg: 'bg-blue-900/30',  label: '보완', labelColor: 'text-blue-400' },
};

// 봇 이름에서 key 추출
function botNameToKey(name) {
  const map = {
    'Livermore Bot': 'jesse_livermore', 'Buffett Bot': 'warren_buffett',
    'Lynch Bot': 'peter_lynch', 'Dalio Bot': 'ray_dalio',
    'Cathie Wood Bot': 'cathie_wood', 'Cathie Bot': 'cathie_wood',
  };
  return map[name] || name;
}

function PersonaDetailSection({ botKey }) {
  const [tab, setTab] = useState('philosophy');
  const { data: persona, loading } = useApi(`/api/persona/${botKey}/persona`, [botKey]);

  if (loading || !persona || !persona.name) return null;

  return (
    <div className="border-b border-gray-700">
      {/* 탭 헤더 */}
      <div className="flex gap-1 p-3 pb-0 overflow-x-auto">
        {PERSONA_TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-xs rounded-t whitespace-nowrap ${
              tab === t.key ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-gray-300'
            }`}
          >{t.label}</button>
        ))}
      </div>

      <div className="p-4 pt-3">
        {tab === 'philosophy' && <PhilosophyTab data={persona} />}
        {tab === 'analysis' && <AnalysisTab data={persona} />}
        {tab === 'trading' && <TradingTab data={persona} />}
        {tab === 'risk' && <RiskTab data={persona} />}
        {tab === 'relationships' && <RelationshipsTab data={persona} />}
      </div>
    </div>
  );
}

function BoldText({ text }) {
  // **키워드** 패턴을 볼드로 렌더
  const parts = (text || '').split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**')
          ? <strong key={i} className="text-white">{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

function PhilosophyTab({ data }) {
  return (
    <div className="space-y-2">
      {(data.philosophy || []).map((item, i) => (
        <div key={i} className="flex gap-2 text-xs text-gray-300">
          <span className="text-blue-400 mt-0.5 flex-shrink-0">{'>'}</span>
          <BoldText text={item} />
        </div>
      ))}
    </div>
  );
}

function AnalysisTab({ data }) {
  const a = data.analysis || {};
  return (
    <div className="space-y-3">
      {a.chart && (
        <div>
          <div className="text-xs text-gray-500 mb-1">차트 분석</div>
          <div className="text-xs text-gray-300 bg-gray-900 rounded p-2">{a.chart}</div>
        </div>
      )}
      {a.indicators && a.indicators.length > 0 && (
        <div>
          <div className="text-xs text-gray-500 mb-1">사용 지표</div>
          <div className="flex flex-wrap gap-1">
            {a.indicators.map((ind, i) => (
              <span key={i} className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">{ind}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TradingTab({ data }) {
  const t = data.trading || {};
  return (
    <div className="space-y-3">
      {t.buy_conditions && t.buy_conditions.length > 0 && (
        <div>
          <div className="text-xs text-green-400 font-semibold mb-1">매수 조건</div>
          <ul className="space-y-1">
            {t.buy_conditions.map((c, i) => (
              <li key={i} className="text-xs text-gray-300 flex gap-2">
                <span className="text-green-500 flex-shrink-0">+</span>
                <BoldText text={c} />
              </li>
            ))}
          </ul>
        </div>
      )}
      {t.sell_conditions && t.sell_conditions.length > 0 && (
        <div>
          <div className="text-xs text-red-400 font-semibold mb-1">매도/손절 조건</div>
          <ul className="space-y-1">
            {t.sell_conditions.map((c, i) => (
              <li key={i} className="text-xs text-gray-300 flex gap-2">
                <span className="text-red-500 flex-shrink-0">-</span>
                <BoldText text={c} />
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 text-xs">
        {t.holding_period && (
          <div>
            <div className="text-gray-500">보유 기간</div>
            <div className="text-gray-300 mt-0.5">{t.holding_period}</div>
          </div>
        )}
        {t.trade_frequency && (
          <div>
            <div className="text-gray-500">매매 빈도</div>
            <div className="text-gray-300 mt-0.5">{t.trade_frequency}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function RiskTab({ data }) {
  const r = data.risk || {};
  return (
    <div className="space-y-3">
      {r.tolerance && (
        <div>
          <div className="text-xs text-gray-500 mb-1">리스크 허용도</div>
          <div className="text-xs text-gray-300 bg-gray-900 rounded p-2">{r.tolerance}</div>
        </div>
      )}
      {r.management && r.management.length > 0 && (
        <div>
          <div className="text-xs text-gray-500 mb-1">리스크 관리 규칙</div>
          <ul className="space-y-1">
            {r.management.map((m, i) => (
              <li key={i} className="text-xs text-gray-300 flex gap-2">
                <span className="text-yellow-500 flex-shrink-0">!</span>
                <BoldText text={m} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RelationshipsTab({ data }) {
  const rels = data.relationships || [];
  if (rels.length === 0) return <div className="text-xs text-gray-500">관계 데이터 없음</div>;

  return (
    <div className="space-y-3">
      {rels.map((rel, i) => {
        const type = classifyRelation(rel.description);
        const style = REL_STYLES[type];
        const key = botNameToKey(rel.bot);
        return (
          <div key={i} className={`border-l-4 ${style.border} ${style.bg} rounded-r-lg p-3`}>
            <div className="flex items-center gap-2 mb-1.5">
              <BotAvatar botName={key} size="sm" />
              <span className="text-sm font-semibold text-gray-200">{rel.bot}</span>
              <span className={`text-xs ${style.labelColor} bg-gray-800 px-1.5 py-0.5 rounded`}>{style.label}</span>
            </div>
            <div className="text-xs text-gray-300">{rel.description}</div>
          </div>
        );
      })}
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 투자 신념 진화
// ────────────────────────────────────────────────────────────
function BeliefEvolution({ botKey }) {
  const { data, loading } = useApi(`/api/persona/${botKey}/evolution`, [botKey]);

  if (loading) return null;
  if (!data || data.status === 'insufficient_data') {
    return (
      <div className="p-4 border-t border-gray-700">
        <h4 className="text-sm font-semibold text-gray-400 mb-2">투자 신념 진화</h4>
        <div className="text-xs text-gray-500">{data?.message || '데이터 부족'}</div>
      </div>
    );
  }

  return (
    <div className="p-4 border-t border-gray-700">
      <h4 className="text-sm font-semibold text-gray-400 mb-2">투자 신념 진화</h4>

      {/* overall_trend */}
      {data.overall_trend && (
        <div className="bg-gray-900 rounded-lg p-3 mb-3 border border-gray-700">
          <div className="text-sm text-blue-400 font-medium">{data.overall_trend}</div>
        </div>
      )}

      {/* insights 타임라인 */}
      {data.insights && data.insights.length > 0 && (
        <div className="relative pl-5 space-y-3">
          <div className="absolute left-2 top-0 bottom-0 w-px bg-gray-700" />
          {data.insights.map((ins, i) => (
            <div key={i} className="relative">
              <div className="absolute left-[-12px] w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-gray-800"
                style={{ top: '0.4rem' }} />
              <div className="ml-2">
                <div className="text-xs font-semibold text-gray-300">{ins.period}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  <span className="text-xs bg-blue-900/50 text-blue-300 px-1.5 py-0.5 rounded">{ins.focus}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">{ins.belief}</div>
                {ins.change && (
                  <div className="text-xs text-yellow-400 mt-0.5">
                    {ins.change}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────
// 페르소나 히스토리 타임라인 (기존 + 개선)
// ────────────────────────────────────────────────────────────
function PersonaTimeline({ selectedBot }) {
  const [botFilter, setBotFilter] = useState(null);
  const [expanded, setExpanded] = useState({});
  const activeBot = selectedBot || botFilter;
  const param = activeBot ? `?bot_name=${encodeURIComponent(activeBot)}&limit=50` : '?limit=50';
  const { data, loading } = useApi(`/api/persona/comments${param}`, [activeBot]);

  const toggle = (i) => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }));

  const DECISION_COLORS = {
    BUY:  { dot: 'bg-green-500', text: 'text-green-400 font-bold' },
    SELL: { dot: 'bg-red-500',   text: 'text-red-400 font-bold' },
    HOLD: { dot: 'bg-gray-500',  text: 'text-gray-400' },
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700">
      <div className="p-3 border-b border-gray-700 flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-400">페르소나 히스토리 타임라인</h3>
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => setBotFilter(null)}
            className={`px-2 py-1 text-xs rounded min-h-[32px] ${!botFilter && !selectedBot ? 'bg-blue-600' : 'bg-gray-700 text-gray-400'}`}
          >전체</button>
          {BOT_INFO.map((b) => (
            <button key={b.key} onClick={() => setBotFilter(b.key)}
              className={`px-2 py-1 text-xs rounded min-h-[32px] flex items-center gap-1 ${
                (botFilter === b.key || selectedBot === b.key) ? 'bg-blue-600' : 'bg-gray-700 text-gray-400'
              }`}
            >
              <BotAvatar botName={b.key} size="sm" className="!w-4 !h-4" />
              {b.initials}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-gray-500">로딩 중...</div>
      ) : !data || data.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500">
          아직 변경 이력 없음
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          <div className="relative pl-6">
            <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-700" />

            {data.map((c, i) => {
              const botInfo = BOT_INFO.find((b) => b.key === c.bot_name) || {};
              const dStyle = DECISION_COLORS[c.decision] || DECISION_COLORS.HOLD;
              const isLong = c.comment && c.comment.length > 80;
              const isOpen = expanded[i];

              return (
                <div key={i} className="relative py-3 pr-3">
                  <div className={`absolute left-1.5 w-3 h-3 rounded-full border-2 border-gray-800 ${dStyle.dot}`}
                    style={{ top: '1rem' }} />

                  <div className="ml-4">
                    <div className="flex items-center gap-2 text-xs mb-1 flex-wrap">
                      <BotAvatar botName={c.bot_name} size="sm" className="!w-5 !h-5" />
                      <span className="font-semibold text-gray-200">{botInfo.label || c.bot_name}</span>
                      {c.ticker && <span className="text-blue-400">{c.ticker}</span>}
                      {c.decision && <span className={dStyle.text}>{c.decision}</span>}
                      <span className="text-gray-600 ml-auto">{fmtKST(c.created_at)}</span>
                    </div>

                    {/* 확신도 바 */}
                    {c.confidence != null && (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex-1 bg-gray-700 rounded-full h-1 max-w-[100px]">
                          <div
                            className={`h-1 rounded-full ${c.confidence >= 70 ? 'bg-green-500' : c.confidence >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(c.confidence, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{Number(c.confidence).toFixed(0)}%</span>
                      </div>
                    )}

                    {/* 코멘트 (접기/펼치기) */}
                    <div className="text-xs text-gray-300 whitespace-pre-wrap">
                      {isLong && !isOpen ? c.comment.slice(0, 80) + '...' : c.comment}
                    </div>
                    {isLong && (
                      <button onClick={() => toggle(i)} className="text-xs text-gray-500 hover:text-gray-300 mt-0.5">
                        {isOpen ? '접기' : '더 보기'}
                      </button>
                    )}

                    <div className="text-xs text-gray-600 mt-0.5">{c.context_type}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
